import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Euro, TrendingUp, Hourglass, ChevronRight } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { fmtMoney, invoiceStatusMeta, placementStatusMeta } from '../components/crm/constants';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BigStat = ({ icon: Icon, label, value, sub, tone, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${tone}`} />
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 mt-1">{sub}</p>
    </CardContent>
  </Card>
);

export default function SalesCommission() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'jobasti_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/commission');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success && res.data) { setData(res.data); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = data?.snapshot;
  const monthly = data?.monthly || [];
  const placements = data?.placements || [];
  const maxMonthly = Math.max(...monthly.map((m) => m.commission), 1);
  const hasMonthly = monthly.some((m) => m.commission > 0);

  return (
    <SalesLayout>
      <div data-testid="commission-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{isAdmin ? 'Commission' : 'My Commission'}</h1>
            <p className="text-sm text-gray-500 mt-1">Earnings from your placements — commission counts as earned when the invoice is paid.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/crm-placements')} data-testid="commission-view-placements-btn">
            View placements <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              Placement tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-06-16-crm-phase3.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <BigStat icon={Euro} label="Earned (YTD)" tone="text-emerald-500"
                value={loading ? '…' : (fmtMoney(s?.earnedYtd ?? 0) || '€0')}
                sub={`From paid invoices · ${fmtMoney(s?.revenueYtd ?? 0) || '€0'} revenue booked YTD`}
                testId="commission-earned-card" />
              <BigStat icon={Hourglass} label="Pending" tone="text-amber-500"
                value={loading ? '…' : (fmtMoney(s?.pending ?? 0) || '€0')}
                sub={`Awaiting invoice / payment · ${fmtMoney(s?.pendingRevenue ?? 0) || '€0'} revenue`}
                testId="commission-pending-card" />
              <BigStat icon={TrendingUp} label="Pipeline Potential" tone="text-blue-500"
                value={loading ? '…' : (fmtMoney(s?.potentialCommission ?? 0) || '€0')}
                sub={`Est. at ${s?.defaultRate ?? 10}% on ${fmtMoney(s?.potentialRevenue ?? 0) || '€0'} of open-job fees`}
                testId="commission-potential-card" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card data-testid="commission-monthly-card">
                <CardHeader className="pb-3"><CardTitle className="text-base">Monthly Commission ({new Date().getFullYear()})</CardTitle></CardHeader>
                <CardContent>
                  {!hasMonthly && <p className="text-sm text-gray-500 py-6 text-center">No commission booked yet this year.</p>}
                  {hasMonthly && (
                    <div className="space-y-2">
                      {monthly.map((m) => (
                        <div key={m.month} className="flex items-center gap-3" data-testid={`commission-month-${m.month}`}>
                          <span className="text-xs text-gray-500 w-8">{MONTHS[m.month]}</span>
                          <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                            <div
                              className="h-full bg-emerald-500/80 rounded transition-all"
                              style={{ width: `${Math.round((m.commission / maxMonthly) * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-20 text-right">{m.commission > 0 ? fmtMoney(m.commission) : '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {isAdmin && data?.perExec ? (
                <Card data-testid="commission-per-exec-card">
                  <CardHeader className="pb-3"><CardTitle className="text-base">By Sales Executive</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-2 font-medium">Executive</th>
                          <th className="px-4 py-2 font-medium text-center">Placements</th>
                          <th className="px-4 py-2 font-medium text-right">Revenue</th>
                          <th className="px-4 py-2 font-medium text-right">Earned</th>
                          <th className="px-4 py-2 font-medium text-right">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.perExec.map((e) => (
                          <tr key={e.userId} className="border-b last:border-0" data-testid={`commission-exec-${e.userId}`}>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{e.name}</td>
                            <td className="px-4 py-2.5 text-center text-gray-600">{e.placements}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{fmtMoney(e.revenue) || '€0'}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-600 font-medium">{fmtMoney(e.earned) || '€0'}</td>
                            <td className="px-4 py-2.5 text-right text-amber-600">{fmtMoney(e.pending) || '€0'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ) : (
                <Card data-testid="commission-how-card">
                  <CardHeader className="pb-3"><CardTitle className="text-base">How commission works</CardTitle></CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <div>• Each placement's fee revenue is <strong>% of salary</strong> or a <strong>fixed fee</strong>.</div>
                    <div>• Your commission is a percentage of that revenue (default {s?.defaultRate ?? 10}%, adjustable per placement).</div>
                    <div>• Commission counts as <strong>earned</strong> once the client invoice is marked <Badge className="bg-emerald-100 text-emerald-700 border-0">Paid</Badge>.</div>
                    <div>• If a candidate leaves within the guarantee period, mark the placement <Badge className="bg-rose-100 text-rose-700 border-0">Fell Out</Badge> — its revenue and commission are reversed.</div>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card data-testid="commission-placements-card">
              <CardHeader className="pb-3"><CardTitle className="text-base">Commission per Placement</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Candidate</th>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium text-right">Revenue</th>
                        <th className="px-4 py-3 font-medium text-center">Rate</th>
                        <th className="px-4 py-3 font-medium text-right">Commission</th>
                        <th className="px-4 py-3 font-medium">Invoice</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && placements.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No placements recorded yet.</td></tr>
                      )}
                      {placements.map((p) => {
                        const inv = invoiceStatusMeta(p.invoice_status);
                        const st = placementStatusMeta(p.status);
                        const fellOut = p.status === 'fell_out';
                        return (
                          <tr key={p.id} className={`border-b last:border-0 ${fellOut ? 'opacity-60' : ''}`} data-testid={`commission-row-${p.id}`}>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{p.candidate_name}</p>
                              <p className="text-xs text-gray-500">{p.job_title}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{p.accountName || '—'}</td>
                            <td className={`px-4 py-3 text-right ${fellOut ? 'line-through text-gray-400' : 'text-gray-900'}`}>{fmtMoney(p.revenue, p.currency) || '—'}</td>
                            <td className="px-4 py-3 text-center text-gray-600">{p.commission_rate}%</td>
                            <td className={`px-4 py-3 text-right font-medium ${fellOut ? 'line-through text-gray-400' : 'text-emerald-600'}`}>{fmtMoney(p.commission_amount, p.currency) || '—'}</td>
                            <td className="px-4 py-3"><Badge className={`${inv.className} border-0`}>{inv.label}</Badge></td>
                            <td className="px-4 py-3"><Badge className={`${st.className} border-0`}>{st.label}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SalesLayout>
  );
}
